import React, { useState } from 'react';
import { X } from 'lucide-react';

const C = {
  primary: 'var(--c-primary)', secondary: 'var(--c-secondary)', bg: 'var(--c-bg)',
  surface: 'var(--c-surface)', border: 'var(--c-border)', text: 'var(--c-text)',
  textMuted: 'var(--c-textMuted)', ok: 'var(--c-ok)', warn: 'var(--c-warn)', danger: 'var(--c-danger)',
};

type SwDup = { name: string; csvCategory: string | null; existingId: number; existingCategory: string | null };

function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let field = '', row: string[] = [], inQ = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQ) { if (ch === '"') { if (text[i + 1] === '"') { field += '"'; i++; } else inQ = false; } else field += ch; }
    else if (ch === '"') inQ = true;
    else if (ch === ',') { row.push(field); field = ''; }
    else if (ch === '\n') { row.push(field); rows.push(row); row = []; field = ''; }
    else if (ch !== '\r') field += ch;
  }
  if (field.length || row.length) { row.push(field); rows.push(row); }
  return rows;
}

function parseSoftwareCsv(text: string): { name: string; category: string | null }[] {
  const rows = parseCsv(text);
  if (!rows.length) return [];
  const header = rows[0].map(h => h.trim().toLowerCase().replace(/\s+/g, ''));
  const nameIdx = header.indexOf('name');
  const catIdx = header.indexOf('category');
  const out: { name: string; category: string | null }[] = [];
  for (let r = 1; r < rows.length; r++) {
    const cells = rows[r];
    if (!cells.length || cells.every(c => !c.trim())) continue;
    const name = ((nameIdx >= 0 ? cells[nameIdx] : cells[0]) ?? '').trim();
    if (!name) continue;
    out.push({ name, category: catIdx >= 0 ? (cells[catIdx]?.trim() || null) : null });
  }
  return out;
}

const EXAMPLE = [
  'Name,Category',
  'Microsoft Excel,Productivity',
  'Microsoft Word,Productivity',
  'Adobe Photoshop,Design',
  'Figma,Design',
  'Slack,Communication',
  'Zoom,Communication',
].join('\n') + '\n';

export const SoftwareImportPanel: React.FC<{ api: any; onClose: () => void; onChanged: () => void }> = ({ api, onClose, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState<string | null>(null);
  const [dups, setDups] = useState<SwDup[]>([]);

  const downloadExample = () => {
    const blob = new Blob([EXAMPLE], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'software-catalog-example.csv';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const onFile = async (file: File | null) => {
    if (!file) return;
    setBusy(true); setSummary(null); setDups([]);
    try {
      const rows = parseSoftwareCsv(await file.text());
      if (!rows.length) { setSummary('No usable rows found — the first line must be a header with a "Name" column.'); return; }
      const res = await api.post('/api/users/software-catalog/import', { items: rows }) as { imported: number; duplicates: SwDup[] };
      setDups(res.duplicates ?? []);
      const dupN = (res.duplicates ?? []).length;
      setSummary(`Imported ${res.imported} new item${res.imported === 1 ? '' : 's'}.` +
        (dupN ? ` ${dupN} name${dupN === 1 ? '' : 's'} already exist — review below.` : ' No duplicates.'));
      onChanged();
    } catch (e: any) { setSummary(`Import failed: ${e?.message ?? 'error'}`); }
    finally { setBusy(false); }
  };

  const resolve = async (d: SwDup, action: 'update' | 'skip') => {
    if (action === 'update') {
      try { await api.patch(`/api/users/software-catalog/${d.existingId}`, { category: d.csvCategory }); onChanged(); }
      catch (e: any) { alert(`Update failed: ${e?.message ?? 'error'}`); return; }
    }
    setDups(prev => prev.filter(x => !(x.existingId === d.existingId && x.name === d.name)));
  };

  const btn = (bg: string, color: string): React.CSSProperties => ({
    background: bg, color, border: bg === 'transparent' ? `1px solid ${C.border}` : 'none',
    borderRadius: 4, padding: '7px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
  });

  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: 18, marginBottom: 18 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 15, color: C.text }}>Import software from CSV</h3>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, lineHeight: 0 }} aria-label="Close">
          <X size={18} />
        </button>
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
        <label style={{ ...btn(C.primary, '#fff'), opacity: busy ? 0.6 : 1, cursor: busy ? 'not-allowed' : 'pointer' }}>
          {busy ? 'Importing…' : 'Choose CSV file'}
          <input type="file" accept=".csv,text/csv" disabled={busy} style={{ display: 'none' }}
            onChange={e => { const f = e.target.files?.[0] ?? null; e.currentTarget.value = ''; onFile(f); }} />
        </label>
        <button onClick={downloadExample} style={btn('transparent', C.primary)}>Download example CSV</button>
        <span style={{ fontSize: 12, color: C.textMuted }}>
          Columns: <strong>Name</strong> (required), <strong>Category</strong> (optional).
        </span>
      </div>

      {summary && (
        <div style={{ fontSize: 13, color: C.text, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, padding: '10px 14px', marginBottom: dups.length ? 12 : 0 }}>{summary}</div>
      )}

      {dups.length > 0 && (
        <div style={{ border: `1px solid ${C.warn}`, borderRadius: 8, padding: 14, background: `${C.warn}10` }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 4 }}>Review duplicates ({dups.length})</div>
          <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 10 }}>
            These names already exist (catalog names are unique). “Update” sets the existing entry’s category to your CSV value; “Skip” leaves it unchanged.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {dups.map(d => (
              <div key={`${d.existingId}-${d.name}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, fontSize: 13, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 6, padding: '8px 12px' }}>
                <span>
                  <strong>{d.name}</strong>{' '}
                  <span style={{ color: C.textMuted }}>
                    — currently {d.existingCategory ? `“${d.existingCategory}”` : 'uncategorized'}
                    {d.csvCategory && d.csvCategory !== d.existingCategory ? ` → CSV: “${d.csvCategory}”` : ''}
                  </span>
                </span>
                <span style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button onClick={() => resolve(d, 'update')} style={btn(C.primary, '#fff')}>Update</button>
                  <button onClick={() => resolve(d, 'skip')} style={btn('transparent', C.textMuted)}>Skip</button>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
